/**
 * Copyright 2020 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: LicenseRef-.amazon.com.-AmznSL-1.0
 * Licensed under the Amazon Software License  http://aws.amazon.com/asl/
 */

import _ from 'lodash';
import React from 'react';
import { observer, inject } from 'mobx-react';
import { makeObservable, action } from 'mobx';
import TimeAgo from 'react-timeago';
import { Header, Label, Breadcrumb, Container, Dropdown } from 'semantic-ui-react';
import purify from 'dompurify';

import { gotoFn, withRouter } from '../../../helpers/routing';
import { swallowError } from '../../../helpers/utils';
import { isStoreError, isStoreReady, isStoreLoading } from '../../../models/BaseStore';
import ErrorBox from '../../helpers/ErrorBox';
import ProgressPlaceHolder from '../../workflow-common/ProgressPlaceholder';
import getUIState from '../../workflow-common/component-states/WorkflowCommonCardState';
import WorkflowDetailTabs from './WorkflowDetailTabs';

// expected props
// - workflowsStore (via injection)
// - userDisplayName (via injection)
// - location (from react router)
class WorkflowDetailPage extends React.Component {
  constructor(props) {
    super(props);

    makeObservable(this, {
      handleVersionChange: action,
    });
  }

  componentDidMount() {
    const store = this.getStore();
    swallowError(store.load());
    store.startHeartbeat();
  }

  componentWillUnmount() {
    const store = this.getStore();
    store.stopHeartbeat();
  }

  getState() {
    return getUIState(`wf-${this.getWorkflowId()}`);
  }

  getStore() {
    const workflowId = this.getWorkflowId();
    return this.props.workflowsStore.getWorkflowStore(workflowId);
  }

  getUserDisplayNameService() {
    return this.props.userDisplayName;
  }

  getWorkflowId() {
    return (this.props.match.params || {}).workflowId;
  }

  getVersionNumber() {
    return parseInt((this.props.match.params || {}).version, 10);
  }

  getWorkflow() {
    const store = this.getStore();
    if (!isStoreReady(store)) return {};
    return store.workflow;
  }

  getVersion() {
    const workflow = this.getWorkflow();
    const num = this.getVersionNumber();

    return workflow.getVersion(num);
  }

  handleVersionChange = ({ value = '' }) => {
    const goto = gotoFn(this);
    const workflowId = this.getWorkflowId();
    goto(`/workflows/published/id/${workflowId}/v/${value}`);
  };

  render() {
    const store = this.getStore();
    let content = null;

    if (isStoreError(store)) {
      content = <ErrorBox error={store.error} className="p0" />;
    } else if (isStoreLoading(store)) {
      content = <ProgressPlaceHolder />;
    } else if (isStoreReady(store)) {
      content = this.renderMain();
    } else {
      content = null;
    }

    return (
      <Container className="mt3">
        {this.renderBreadcrumb()}
        {content}
      </Container>
    );
  }

  renderBreadcrumb() {
    const workflowId = this.getWorkflowId();
    const goto = gotoFn(this);

    return (
      <Breadcrumb className="block mb3">
        <Breadcrumb.Section link onClick={() => goto('/workflows/published')}>
          Workflows
        </Breadcrumb.Section>
        <Breadcrumb.Divider icon="right angle" />
        <Breadcrumb.Section active>{workflowId}</Breadcrumb.Section>
      </Breadcrumb>
    );
  }

  renderMain() {
    const version = this.getVersion();
    const { id, title, updatedAt, updatedBy, descHtml } = version;
    const displayNameService = this.getUserDisplayNameService();
    const isSystem = displayNameService.isSystem(updatedBy);
    const by = () => (isSystem ? '' : <span className="ml1">by {displayNameService.getDisplayName(updatedBy)}</span>);

    const uiState = this.getState();

    /* eslint-disable react/no-danger */
    return (
      <>
        <div className="flex mb2">
          <Header as="h3" color="grey" className="mt0 flex-auto ellipsis">
            <Label color="teal" className="ml0 mr1">
              Workflow
            </Label>
            {title}
            <Header.Subheader className="fs-9 color-grey mt1">
              <div>
                updated <TimeAgo date={updatedAt} /> {by()}
              </div>
            </Header.Subheader>
          </Header>
          <div className="ml1">
            <span className="ellipsis pr1 fs-9 breakout color-grey">{id}</span> {this.renderVersionDropdown()}
          </div>
        </div>
        <div className="mb3">
          <div dangerouslySetInnerHTML={{ __html: purify.sanitize(descHtml) }} />
        </div>
        <WorkflowDetailTabs uiState={uiState} workflow={version} />
      </>
    );
    /* eslint-enable react/no-danger */
  }

  renderVersionDropdown() {
    const workflow = this.getWorkflow();
    const versions = workflow.versionNumbers;
    const currentVersion = this.getVersionNumber();
    const options = _.map(versions, version => ({ text: `v${version}`, value: version }));

    if (versions.length === 1) return <span className="bold color-grey pr2">v{currentVersion}</span>;

    return (
      <Dropdown
        className="color-grey"
        inline
        options={options}
        value={currentVersion}
        onChange={(_e, data) => this.handleVersionChange(data)}
      />
    );
  }
}

export default inject('workflowsStore', 'userDisplayName')(withRouter(observer(WorkflowDetailPage)));
